import type { OrgChatAttachmentInput } from '@/components/org-chat/org-chat-types';
import type { ReferenceSelectionCategory } from '@/components/org-chat/reference-selection-policy';
import type { BackgroundUploadJob } from './background-upload-types';
import type {
  NewPersistedUpload,
  PersistedUpload,
} from './persisted-upload-record';
import {
  persistableUploadSession,
  type UploadSessionJournalEntry,
} from './upload-session-journal';

/**
 * Pure helpers behind the background upload manager (#4844): the record a
 * bound job keeps in browser storage, the job a record resumes as, and which
 * records a signed-in member may resume in a workspace.
 */

/** What browser storage keeps for a job: its sender, never a session token. */
export function newPersistedUploadRecord(
  job: Pick<
    BackgroundUploadJob,
    'uploadId' | 'orgSlug' | 'orgThreadId' | 'routeRef' | 'pendingSendId'
  > & { actorId: string },
  args: {
    file: File;
    attachment: OrgChatAttachmentInput;
    category?: ReferenceSelectionCategory;
  },
  session: UploadSessionJournalEntry | undefined
): NewPersistedUpload {
  const multipart = session ? persistableUploadSession(session) : null;
  return {
    uploadId: job.uploadId,
    actorId: job.actorId,
    orgSlug: job.orgSlug,
    orgThreadId: job.orgThreadId,
    ...(job.routeRef ? { routeRef: job.routeRef } : {}),
    pendingSendId: job.pendingSendId,
    filename: args.attachment.filename,
    mimeType: args.attachment.mimeType,
    sizeBytes: args.file.size,
    lastModified: args.file.lastModified,
    kind: args.attachment.kind,
    ...(args.category ? { referenceCategory: args.category } : {}),
    ...(multipart ? { multipart } : {}),
  };
}

/** The job a persisted record resumes as. */
export function backgroundJobMeta(
  record: PersistedUpload
): Omit<BackgroundUploadJob, 'state'> {
  return {
    uploadId: record.uploadId,
    pendingSendId: record.pendingSendId,
    orgThreadId: record.orgThreadId,
    ...(record.routeRef ? { routeRef: record.routeRef } : {}),
    orgSlug: record.orgSlug,
    filename: record.filename,
    sizeBytes: record.sizeBytes,
  };
}

/**
 * The records this member may resume in this workspace: their own, here.
 * Another member's records on a shared browser are never touched; rows
 * written before the sender was recorded can be resumed by nobody.
 */
export function selectResumableRecords(
  records: ReadonlyArray<PersistedUpload>,
  scope: { actorId: string; orgSlug: string },
  isTracked: (uploadId: string) => boolean
): { resumable: PersistedUpload[]; unowned: PersistedUpload[] } {
  return {
    resumable: records.filter(
      (record) =>
        record.actorId === scope.actorId &&
        record.orgSlug === scope.orgSlug &&
        !isTracked(record.uploadId)
    ),
    unowned: records.filter((record) => !record.actorId),
  };
}

/** Records grouped by the task they wait in. */
export function recordsByThread(
  records: ReadonlyArray<PersistedUpload>
): Map<string, PersistedUpload[]> {
  const byThread = new Map<string, PersistedUpload[]>();
  for (const record of records)
    byThread.set(record.orgThreadId, [
      ...(byThread.get(record.orgThreadId) ?? []),
      record,
    ]);
  return byThread;
}

/** The jobs a workspace's UI shows: only that workspace's. */
export function jobsInWorkspace<T extends Pick<BackgroundUploadJob, 'orgSlug'>>(
  jobs: ReadonlyArray<T>,
  orgSlug: string
): T[] {
  return jobs.filter((job) => job.orgSlug === orgSlug);
}

/**
 * Who the manager works for: the signed-in member, null once signed out,
 * undefined while that is not known yet (loading, a viewer refetch), so a
 * passing blip never stops anyone's uploads.
 */
export function backgroundUploadActor(auth: {
  user?: { userId?: string } | null;
  isSignedOut: boolean;
}): string | null | undefined {
  if (auth.isSignedOut) return null;
  return auth.user?.userId ?? undefined;
}

/** Bytes a progress report must move before it is worth a write. */
const PROGRESS_STEP_BYTES = 4 * 1024 * 1024;

/**
 * The progress worth reporting, one batch per send: uploads that moved at
 * least a step since their last report. Marks each one reported.
 */
export function progressUpdatesBySend(
  jobs: Iterable<
    Pick<BackgroundUploadJob, 'uploadId' | 'pendingSendId' | 'state'> & {
      reportedBytes: number;
    }
  >,
  loadedBytesOf: (uploadId: string) => number
): Map<string, Array<{ uploadId: string; uploadedBytes: number }>> {
  const bySend = new Map<
    string,
    Array<{ uploadId: string; uploadedBytes: number }>
  >();
  for (const job of jobs) {
    if (job.state !== 'uploading' || !job.pendingSendId) continue;
    const loaded = loadedBytesOf(job.uploadId);
    if (Math.abs(loaded - job.reportedBytes) < PROGRESS_STEP_BYTES) continue;
    job.reportedBytes = loaded;
    bySend.set(job.pendingSendId, [
      ...(bySend.get(job.pendingSendId) ?? []),
      { uploadId: job.uploadId, uploadedBytes: loaded },
    ]);
  }
  return bySend;
}
